'use client';

import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { useWorkspaceNavigate } from './useWorkspaceNavigate';

export interface WorkspaceLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** In-workspace target, e.g. `/workspace/TLT/settings/item-master`. */
  href: string;
  /** Replace the history entry instead of pushing. */
  replace?: boolean;
}

/**
 * In-workspace link (spec: workspace-static-spa-routing).
 *
 * Renders a real `<a href>` so it keeps every native affordance — hover URL,
 * right-click → open, and Ctrl/Cmd/Shift/middle-click → open in a new tab
 * (which SWA serves: the rewrite loads the shell and the client resolves the
 * view). Only a plain left-click is intercepted and turned into a History-API
 * navigation, avoiding the static-export RSC reload loop.
 *
 * Use this for intra-workspace targets only. For links that LEAVE `/workspace`,
 * use `next/link` / `next/navigation`.
 */
export const WorkspaceLink = forwardRef<HTMLAnchorElement, WorkspaceLinkProps>(
  function WorkspaceLink({ href, replace, target, onClick, children, ...rest }, ref) {
    const navigate = useWorkspaceNavigate();

    const isInShell = (h: string) =>
      h.split('?')[0].split('#')[0].startsWith('/workspace/');

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      // Let the browser handle modified clicks, non-left buttons, and new-tab
      // targets natively.
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (target && target !== '_self') return;
      // Only intercept in-shell targets; anything that leaves `/workspace`
      // (e.g. `/main`) does a real navigation.
      if (!isInShell(href)) return;

      event.preventDefault();
      navigate(href, { replace });
    };

    return (
      <a ref={ref} href={href} target={target} onClick={handleClick} {...rest}>
        {children}
      </a>
    );
  },
);
